/**
 * OrgPicker — sidebar dropdown that lets a multi-org user switch the active
 * org for their session (ads-iq parity).
 *
 * - Hidden when the user belongs to <2 orgs AND isn't a SuperAdmin.
 * - SuperAdmins always see it, so the "All organisations" entry is reachable.
 * - Clicking a different org → POST /api/me/active-org (rotates the rq_session
 *   JWT cookie + stamps users.last_active_org_id), then a hard reload to
 *   /portfolio in the new org's scope.
 * - "All organisations" (SuperAdmin only) does NOT switch the JWT — it routes
 *   to /portfolio?scope=all, which reads brands across every org via
 *   GET /api/brands?scope=all.
 */
import { useState, useRef, useEffect } from "react";
import { Check, ChevronDown, Globe, Loader2 } from "lucide-react";

import { useMyOrgs, useSetActiveOrg, type MyOrg } from "@/lib/myOrgs";
import { useAccess } from "@/auth/AccessContext";

function RoleBadge({ role }: { role: string }) {
  const cls =
    role === "owner"
      ? "bg-blue-100 text-blue-700"
      : role === "admin"
        ? "bg-purple-100 text-purple-700"
        : "bg-gray-100 text-gray-600";
  return (
    <span
      className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium uppercase ${cls}`}
    >
      {role}
    </span>
  );
}

export function OrgPicker() {
  const { data, isLoading } = useMyOrgs();
  const { isSuperAdmin } = useAccess();
  const setActive = useSetActiveOrg();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  if (isLoading || !data) return null;
  const orgs = data.items;
  // Hide if the user has one org and isn't a SuperAdmin (no churn). SuperAdmins
  // always see it so the "All organisations" entry is reachable.
  if (orgs.length < 2 && !isSuperAdmin) return null;
  const active: MyOrg | undefined = orgs.find((o) => o.is_active) ?? orgs[0];

  const allOrgsActive =
    typeof window !== "undefined" &&
    window.location.pathname === "/portfolio" &&
    new URLSearchParams(window.location.search).get("scope") === "all";

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={setActive.isPending}
        className="w-full flex items-center justify-between gap-2 rounded-md border border-border bg-bg-card px-2 py-1.5 text-left text-xs hover:bg-bg-hover disabled:opacity-60"
        title="Switch organisation"
      >
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase text-text-muted font-semibold tracking-wide">
            Organisation
          </div>
          <div className="text-xs font-medium text-text-primary truncate">
            {allOrgsActive ? "All organisations" : (active?.name ?? "—")}
          </div>
        </div>
        {setActive.isPending ? (
          <Loader2 size={14} className="animate-spin text-text-muted shrink-0" />
        ) : (
          <ChevronDown size={14} className="text-text-muted shrink-0" />
        )}
      </button>

      {open && (
        <div className="absolute bottom-full left-0 right-0 mb-1 z-50 rounded-lg border border-border-lighter bg-bg-card shadow-lg overflow-hidden">
          <div className="px-3 py-2 text-[10px] uppercase tracking-wide text-text-muted border-b border-border-lighter">
            {isSuperAdmin ? "Switch organisation" : "Your organisations"}
          </div>
          <div className="max-h-64 overflow-y-auto">
            {isSuperAdmin && (
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  if (typeof window !== "undefined") {
                    window.location.href = "/portfolio?scope=all";
                  }
                }}
                className="w-full flex items-center gap-2 px-3 py-2 text-left text-xs hover:bg-bg-hover border-b border-border-lighter"
              >
                <Globe size={14} className="text-text-muted shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-text-primary truncate">
                    All organisations
                  </div>
                  <div className="text-[11px] text-text-muted truncate">
                    Cross-org Portfolio · SuperAdmin
                  </div>
                </div>
                {allOrgsActive && (
                  <Check size={14} className="text-accent shrink-0" />
                )}
              </button>
            )}
            {orgs.map((o) => (
              <button
                key={o.id}
                type="button"
                onClick={() => {
                  if (o.is_active) {
                    setOpen(false);
                    return;
                  }
                  setActive.mutate(o.id);
                }}
                disabled={setActive.isPending}
                className="w-full flex items-center gap-2 px-3 py-2 text-left text-xs hover:bg-bg-hover disabled:opacity-60"
              >
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-text-primary truncate">
                    {o.name}
                  </div>
                </div>
                <RoleBadge role={o.role} />
                {o.is_active && <Check size={14} className="text-accent shrink-0" />}
              </button>
            ))}
          </div>
          {setActive.isError && (
            <div className="px-3 py-2 text-[11px] text-priority-critical border-t border-border-lighter">
              {setActive.error instanceof Error
                ? setActive.error.message
                : "Failed to switch"}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
